import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebase/config';
import { DataPort, GetSensorDataQuery } from '../../domain/ports/DataPort';
import { RepositoryError } from '../../domain/errors';
import { toTimestamp } from '../../domain/utils/dateUtils';
import { ILoggerService } from '../../domain/services/LoggerService';

export class FirebaseDataAdapter implements DataPort {
  private readonly prefix = 'data';
  private readonly logger: ILoggerService;

  constructor(logger: ILoggerService) {
    this.logger = logger;
  }

  async getSensorData(query: GetSensorDataQuery): Promise<any> {
    const startTime = Date.now();
    const functionName = `${this.prefix}-getSensorData`;
    this.logger.logRepositoryRequest('DataAdapter', 'getSensorData', { functionName, query });
    
    try {
      const getSensorDataFn = httpsCallable(functions, functionName);
      
      // Convert dates to timestamp milliseconds for the backend
      const queryWithTimestamps = {
        ...query,
        startDate: query.startDate ? toTimestamp(query.startDate) : undefined,
        endDate: query.endDate ? toTimestamp(query.endDate) : undefined
      };
      
      const result = await getSensorDataFn(queryWithTimestamps);
      
      if (!result.data) {
        throw RepositoryError.queryFailed();
      }

      const duration = Date.now() - startTime;
      this.logger.logRepositoryResponse('DataAdapter', 'getSensorData', duration, { 
        hasData: !!result.data,
        dataSize: result.data ? Object.keys(result.data).length : 0
      });

      // Return raw Firebase response - let domain service handle transformation
      return result.data;
    } catch (error: any) {
      const duration = Date.now() - startTime;
      this.logger.logRepositoryError('DataAdapter', 'getSensorData', duration, error);
      console.error('Get sensor data error:', error);
      
      if (error.details?.code) {
        throw new RepositoryError(error.details.code);
      }
      
      throw RepositoryError.queryFailed();
    }
  }
}